/**
 * A MASKED element (KPR-1348): `options.mask: true` on a short text, a long
 * text or a number. Its value sits behind a fixed-length mask on every screen
 * until the person chooses Show or Copy (components/common/MaskedValue.js),
 * and is hidden while it is typed (components/Input.js).
 *
 * A DISPLAY setting and nothing more. The value is stored, read, written and
 * exported plain — the API and an assistant read it like any other — and it
 * is never called secure or encrypted (that is KPR-1349, on hold). Free text
 * never matches it and no title prints it; those halves are the API's
 * (keepr-api utils/maskedElements.js, the same rule).
 *
 * Showing and copying are the viewer's own: nothing is sent, nothing recorded.
 */

/** The option key on a card definition's element. */
export const MASK_KEY = 'mask';

/** The types a value may be masked on (the owner's scope, 2026-10-26). */
export const MASKABLE_TYPES = Object.freeze(['text-small', 'text-large', 'number']);

/**
 * What a masked value reads as: always the same eight dots, whatever the
 * value's length, so the mask says nothing about what is behind it.
 */
export const MASK_TEXT = '••••••••';

export const isMaskableType = dataType => MASKABLE_TYPES.includes(dataType);

/**
 * Is this element masked? Its own `mask: true` on a type that takes it — an
 * inert key on another type masks nothing. Reads a card definition's element
 * (`options.mask`) and the flat schema element (`masked`) alike.
 */
export const isMaskedElement = (element) => {
	if (!element || typeof element !== 'object' || !isMaskableType(element.dataType)) return false;
	if (element.masked === true) return true;
	return element.options?.[MASK_KEY] === true;
};

/** Is there a value to mask? An empty element shows as empty, never as a mask. */
export const hasMaskableValue = value => !(value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length));
